import type { EditorJsBlock, EditorJsData } from '@/types'
import '@/components/news/newsArticle.css'

/**
 * Чистый рендерер блоков Editor.js для публичного лэндинга —
 * без загрузки самого Editor.js в бандл. Инлайн-HTML в текстах
 * санитизирован на бэкенде (NewsBlockSanitizer).
 */

interface NewsBlocksProps {
  body: EditorJsData
}

interface ListItem {
  content?: string
  items?: ListItem[]
}

function renderList(items: unknown[], style: string, key: number): JSX.Element {
  const Tag = style === 'ordered' ? 'ol' : 'ul'
  return (
    <Tag key={key}>
      {items.map((item, index) => {
        if (typeof item === 'string') {
          return <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
        }
        const listItem = item as ListItem
        return (
          <li key={index}>
            <span dangerouslySetInnerHTML={{ __html: listItem.content ?? '' }} />
            {Array.isArray(listItem.items) && listItem.items.length > 0 &&
              renderList(listItem.items, style, index)}
          </li>
        )
      })}
    </Tag>
  )
}

function renderBlock(block: EditorJsBlock, index: number): JSX.Element | null {
  const data = block.data as Record<string, unknown>

  switch (block.type) {
    case 'header': {
      const level = typeof data.level === 'number' ? data.level : 2
      const text = typeof data.text === 'string' ? data.text : ''
      // На странице новости h1 занят заголовком — блоки начинаются с h2
      const Tag = (`h${Math.min(Math.max(level, 2), 4)}`) as 'h2' | 'h3' | 'h4'
      return <Tag key={index} dangerouslySetInnerHTML={{ __html: text }} />
    }
    case 'paragraph': {
      const text = typeof data.text === 'string' ? data.text : ''
      return <p key={index} dangerouslySetInnerHTML={{ __html: text }} />
    }
    case 'list': {
      const items = Array.isArray(data.items) ? data.items : []
      const style = typeof data.style === 'string' ? data.style : 'unordered'
      return renderList(items, style, index)
    }
    case 'image': {
      const file = data.file as { url?: string } | undefined
      const url = file?.url ?? (typeof data.url === 'string' ? data.url : undefined)
      if (!url) return null
      const caption = typeof data.caption === 'string' ? data.caption : ''
      return (
        <figure key={index} className="news-article-figure">
          <img src={url} alt={caption.replace(/<[^>]*>/g, '')} loading="lazy" />
          {caption && <figcaption dangerouslySetInnerHTML={{ __html: caption }} />}
        </figure>
      )
    }
    case 'quote': {
      const text = typeof data.text === 'string' ? data.text : ''
      const caption = typeof data.caption === 'string' ? data.caption : ''
      return (
        <blockquote key={index}>
          <span dangerouslySetInnerHTML={{ __html: text }} />
          {caption && <cite dangerouslySetInnerHTML={{ __html: caption }} />}
        </blockquote>
      )
    }
    default:
      return null
  }
}

export function NewsBlocks({ body }: NewsBlocksProps) {
  return (
    <div className="news-article-body">
      {(body.blocks ?? []).map((block, index) => renderBlock(block, index))}
    </div>
  )
}
